import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../layouts/DashboardLayout';
import {
  getWaTemplates, createWaTemplate, updateWaTemplate,
  deleteWaTemplate, syncWaTemplates, sendWaTemplate, getWaAccounts
} from '../../../services/api';
import { toast } from 'sonner';
import {
  FileText, Plus, Edit2, Trash2, RefreshCw, Send, Users, Settings,
  X, MessageCircle, Inbox, Bot, Zap, Bell, Download
} from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Cuentas', path: '/superadmin/whatsapp', icon: MessageCircle },
  { label: 'Inbox', path: '/superadmin/whatsapp/inbox', icon: Inbox },
  { label: 'Auto-respuestas', path: '/superadmin/whatsapp/auto-replies', icon: Bot },
  { label: 'Plantillas', path: '/superadmin/whatsapp/templates', icon: FileText },
  { label: 'Resp. Rapidas', path: '/superadmin/whatsapp/quick-replies', icon: Zap },
  { label: 'Contactos', path: '/superadmin/whatsapp/contacts', icon: Users },
  { label: 'Eventos', path: '/superadmin/whatsapp/event-settings', icon: Settings },
  { label: 'Notificaciones', path: '/superadmin/whatsapp/notifications', icon: Bell },
];

const STATUS_COLORS = {
  APPROVED: 'bg-green-50 text-green-700',
  PENDING: 'bg-amber-50 text-amber-700',
  REJECTED: 'bg-red-50 text-red-700',
};

const EMPTY_FORM = {
  account_id: '', template_name: '', language: 'es_MX',
  category: 'UTILITY', status: 'APPROVED', components: [],
  parameter_count: 0, meta_template_id: '',
};

const WhatsAppTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState('');
  const [sendModal, setSendModal] = useState(null);
  const [sendForm, setSendForm] = useState({ to: '', parameters: [] });
  const [sendingTpl, setSendingTpl] = useState(false);
  const [filterAccount, setFilterAccount] = useState('');

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterAccount) params.account_id = filterAccount;
      const res = await getWaTemplates(params);
      setTemplates(res.data?.templates || []);
    } catch { toast.error('Error cargando plantillas'); }
    finally { setLoading(false); }
  }, [filterAccount]);

  useEffect(() => {
    loadTemplates();
    getWaAccounts().then(r => setAccounts(r.data?.accounts || [])).catch(() => {});
  }, [loadTemplates]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, account_id: accounts.find(a => a.status === 'active')?.id || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.account_id || !form.template_name) {
      toast.error('Completa cuenta y nombre de template');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateWaTemplate(editing.id, {
          category: form.category, status: form.status,
          parameter_count: form.parameter_count, meta_template_id: form.meta_template_id || null,
        });
        toast.success('Template actualizado');
      } else {
        await createWaTemplate(form);
        toast.success('Template registrado');
      }
      setShowModal(false);
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando');
    } finally { setSaving(false); }
  };

  const handleSync = async (accountId) => {
    setSyncing(accountId);
    try {
      const res = await syncWaTemplates(accountId);
      toast.success(`Sincronizados: ${res.data.synced} templates`);
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error sincronizando');
    } finally { setSyncing(''); }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Eliminar template "${tpl.template_name}"?`)) return;
    try {
      await deleteWaTemplate(tpl.id);
      toast.success('Template eliminado');
      loadTemplates();
    } catch { toast.error('Error eliminando'); }
  };

  const openSend = (tpl) => {
    setSendModal(tpl);
    setSendForm({ to: '', parameters: Array(tpl.parameter_count || 0).fill('') });
  };

  const handleSend = async () => {
    if (!sendForm.to.trim()) { toast.error('Ingresa un telefono'); return; }
    setSendingTpl(true);
    try {
      await sendWaTemplate(sendModal.id, {
        to: sendForm.to.trim(),
        parameters: sendForm.parameters.filter(p => p),
      });
      toast.success('Template enviado');
      setSendModal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error enviando');
    } finally { setSendingTpl(false); }
  };

  const getAccountName = (id) => accounts.find(a => a.id === id)?.display_name || id?.slice(0, 8);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Sub-nav */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b border-slate-200">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const active = item.path === '/superadmin/whatsapp/templates';
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  active ? 'bg-green-100 text-green-700 font-medium' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}>
                <Icon className="w-4 h-4" /> {item.label}
              </button>
            );
          })}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Plantillas Meta</h1>
              <p className="text-sm text-slate-500">Plantillas HSM aprobadas por Meta</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <select value={filterAccount} onChange={e => setFilterAccount(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-600">
              <option value="">Todas las cuentas</option>
              {accounts.filter(a => a.status === 'active').map(a => (
                <option key={a.id} value={a.id}>{a.display_name}</option>
              ))}
            </select>
            {accounts.filter(a => a.status === 'active').map(a => (
              <button key={a.id} onClick={() => handleSync(a.id)} disabled={syncing === a.id}
                className="flex items-center gap-1.5 px-3 py-2 bg-teal-100 text-teal-700 text-sm rounded-lg hover:bg-teal-200 disabled:opacity-50">
                <Download className={`w-4 h-4 ${syncing === a.id ? 'animate-spin' : ''}`} />
                Sync {a.display_name}
              </button>
            ))}
            <button onClick={openCreate}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
              <Plus className="w-4 h-4" /> Registrar Manual
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400">Cargando...</div>
          ) : templates.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No hay plantillas</p>
              <p className="text-sm mt-1">Sincroniza desde Meta o registra manualmente</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium text-slate-600">Nombre</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Idioma</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Categoria</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Estado</th>
                    <th className="px-4 py-3 font-medium text-slate-600 text-center">Params</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Cuenta</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {templates.map((tpl) => (
                    <tr key={tpl.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900 font-mono text-xs">{tpl.template_name}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{tpl.language}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{tpl.category}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[tpl.status] || 'bg-slate-100 text-slate-600'}`}>
                          {tpl.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-slate-600">{tpl.parameter_count || 0}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{getAccountName(tpl.account_id)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {tpl.status === 'APPROVED' && (
                            <button onClick={() => openSend(tpl)}
                              className="p-1.5 rounded-lg hover:bg-green-50 text-slate-500 hover:text-green-600" title="Enviar">
                              <Send className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => { setEditing(tpl); setForm({ ...tpl }); setShowModal(true); }}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-blue-600">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(tpl)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">{editing ? 'Editar Template' : 'Registrar Template'}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {!editing && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Cuenta *</label>
                    <select value={form.account_id} onChange={e => setForm(p => ({ ...p, account_id: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                      <option value="">Seleccionar...</option>
                      {accounts.filter(a => a.status === 'active').map(a => (
                        <option key={a.id} value={a.id}>{a.display_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nombre del template *</label>
                    <input value={form.template_name} onChange={e => setForm(p => ({ ...p, template_name: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                      placeholder="order_confirmation" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Idioma</label>
                    <input value={form.language} onChange={e => setForm(p => ({ ...p, language: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Categoria</label>
                  <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    <option value="UTILITY">UTILITY</option>
                    <option value="MARKETING">MARKETING</option>
                    <option value="AUTHENTICATION">AUTHENTICATION</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Estado</label>
                  <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    <option value="APPROVED">APPROVED</option>
                    <option value="PENDING">PENDING</option>
                    <option value="REJECTED">REJECTED</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Parametros del body</label>
                <input type="number" value={form.parameter_count}
                  onChange={e => setForm(p => ({ ...p, parameter_count: parseInt(e.target.value) || 0 }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" min={0} />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-slate-200">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200">Cancelar</button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Template Modal */}
      {sendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">Enviar Template</h2>
              <button onClick={() => setSendModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Template</p>
                <p className="font-mono text-sm font-medium text-slate-800">{sendModal.template_name}</p>
                <p className="text-xs text-slate-400">{sendModal.language} | {sendModal.category}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Telefono destino *</label>
                <input value={sendForm.to} onChange={e => setSendForm(p => ({ ...p, to: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="521234567890" />
              </div>
              {sendForm.parameters.map((param, idx) => (
                <div key={idx}>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Parametro {idx + 1}</label>
                  <input value={param}
                    onChange={e => {
                      const newParams = [...sendForm.parameters];
                      newParams[idx] = e.target.value;
                      setSendForm(p => ({ ...p, parameters: newParams }));
                    }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    placeholder={`Valor para {{${idx + 1}}}`} />
                </div>
              ))}
              <button onClick={handleSend} disabled={sendingTpl || !sendForm.to.trim()}
                className="w-full px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
                <Send className="w-4 h-4" /> {sendingTpl ? 'Enviando...' : 'Enviar Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default WhatsAppTemplates;
